import React, { useState, useEffect } from "react";
import { useOpsContext } from "../../../ContextsAndProvider/OpsContext";
import OpsOrganizationPractitionerSelector from "./OpsOrganizationPractitionerSelector";
import { InfoOutlined } from "@mui/icons-material";
import { Col, Row, Table } from "react-bootstrap";
import { InputLabel, Skeleton, TableCell, Tooltip } from "@mui/material";
import Select from "react-select";
import { getActiveQueue } from "../../apps/api/WaitingRoom";
import { getPractitionerDataForQueue } from "../../apps/api/AppointmentRequests";
import { failed } from "../../common/Toastify";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import { ViewDate } from "../../../utils/DateSupport";
import moment from "moment";
import { VisibilityOutlined } from "@mui/icons-material";
import OpsViewQueueModal from "./OpsViewQueueModal";
import Video from "../../../assets/images/Video.svg";
import Audio from "../../../assets/images/Audio.svg";
import "./OpsDashboard.css";

const OpsDashboardQueue = () => {
  const { selectedOrganization, selectedPractitioner, organizations } = useOpsContext();
  const [queuePatients, setQueuePatients] = useState([]);
  const [searchFilterData, setSearchFilterData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState({
    data: "arrived",
    value: "arrived",
    label: "Arrived",
  });
  const [viewModalShow, setViewModalShow] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const rowsPerPage = 10;

  const handleViewClick = (record) => {
    setSelectedRecord(record);
    setViewModalShow(true);
  };

  const selectedOrgName = organizations?.find(
    (org) => org.id === selectedOrganization
  )?.name;

  const fetchQueueData = async () => {
    if (!selectedPractitioner?.value || !selectedOrganization) {
      return;
    }

    setIsLoading(true);
    try {
      const userRes = await getPractitionerDataForQueue({
        practitionerId: selectedPractitioner.value,
      });
      
      const practitionerData = userRes?.result;
      const scope = practitionerData?.scopeOfPractice;

      if (!scope || !Array.isArray(scope) || scope.length === 0) {
        setQueuePatients([]);
        setSearchFilterData([]);
        setIsLoading(false);
        return;
      }

      const encodedScopeOfPractice = encodeURIComponent(JSON.stringify(scope));

      const res = await getActiveQueue({
        scopeOfPractice: encodedScopeOfPractice,
        status: selectedStatus?.value,
        orgId: selectedOrganization,
      });

      const sortedData = sortByPriorityAndStartDate(res?.result || []);
      setQueuePatients(sortedData);
      setSearchFilterData(sortedData);
    } catch (err) {
      failed(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPractitioner && selectedOrganization) {
      fetchQueueData();
    }
  }, [selectedStatus, selectedOrganization, selectedPractitioner]);

  const sortByPriorityAndStartDate = (data) => {
    if (!Array.isArray(data)) return [];
    return data.sort((a, b) => {
      const priorityComparison = a.priority - b.priority;
      if (priorityComparison === 0) {
        return new Date(a.period.start) - new Date(b.period.start);
      }
      return priorityComparison;
    });
  };

  const calculateWaitTime = (arrivalTimestamp) => {
    const currentTime = new Date();
    const currentTimestamp = currentTime.getTime();
    const waitTimeInMinutes = Math.floor(
      (currentTimestamp - arrivalTimestamp) / (1000 * 60)
    );
    return `${waitTimeInMinutes}`;
  };

  const capitalizeWords = (str) => {
    return str
      ? str
          .toLowerCase()
          .replace(/(^|\s)\S/g, (firstLetter) => firstLetter.toUpperCase())
      : "";
  };

  const statusOptions = [
    { status: "arrived", value: "arrived" },
    { status: "in-progress-waitingroom", value: "in-progress-waitingroom" },
    { status: "in-progress-appointment", value: "in-progress-appointment" },
    { status: "parked", value: "parked" },
    { status: "follow-up", value: "follow-up" },
  ].map((option) => ({
    value: option.value,
    label: capitalizeWords(option.status),
    data: option.status,
  }));

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
  };

  return (
    <div className="ops-dashboard-page-wrap">
      <section className="common-listing ops-dashboard-page">
        <div className="heading-wrap ops-dashboard-heading">
          <h1>Practitioner View – Waiting Room</h1>
        </div>
        <OpsOrganizationPractitionerSelector />
        {selectedPractitioner && selectedOrganization && (
          <>
            <div className="ops-read-only-banner">
              <InfoOutlined />
              <span>
                Read-only view - You are viewing this dashboard as{" "}
                {selectedPractitioner?.label} from {selectedOrgName}
              </span>
            </div>
            <div className="ops-dashboard-content">
              <div className="heading-wrap h-change">
                <Row>
                  <Col>
                    <InputLabel
                      id="status-select-label"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {"Status"}
                    </InputLabel>
                    <div style={{ width: "150px" }}>
                      <Select
                        onChange={handleStatusChange}
                        isMulti={false}
                        name="status"
                        options={statusOptions}
                        className="search-select"
                        classNamePrefix="select"
                        value={selectedStatus}
                        isClearable={false}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="table-wrap">
                <Table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Priority</th>
                      <th style={{ textAlign: "center" }}>Patient Name</th>
                      <th style={{ textAlign: "center" }}>Organization</th>
                      <th style={{ textAlign: "center" }}>Location</th>
                      <th style={{ textAlign: "center" }}>Reason for Call</th>
                      {selectedStatus?.value !== "in-progress-appointment" ? (
                        <th style={{ textAlign: "center" }}>Wait Time</th>
                      ) : (
                        <th style={{ textAlign: "center" }}>Arrival Time</th>
                      )}
                      <th style={{ textAlign: "center" }}>Notes</th>
                      <th style={{ textAlign: "center" }}>Channel</th>
                      <th style={{ textAlign: "center" }}>Practitioner</th>
                      <th style={{ textAlign: "center" }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((val) => (
                        <tr key={val}>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                            <td key={i}>
                              <Skeleton animation="wave" />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : searchFilterData?.length > 0 ? (
                      searchFilterData
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        ?.map((data) => {
                          const waitTime = calculateWaitTime(data?.createdAt);
                          return (
                            <tr key={data?.id}>
                              <td style={{ textTransform: "capitalize" }}>
                                {data?.intakeQuestions?.intakePriorityReason || "-"}
                              </td>
                              <td>{data?.priority}</td>
                              <td style={{ textAlign: "center", textTransform: "capitalize" }}>
                                {data?.subject?.display}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {selectedOrgName}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {data?.patientLocation}
                              </td>
                              <td
                                style={{
                                  wordBreak: "break-word",
                                  maxWidth: "200px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {data?.intakeQuestions?.intakeReason ||
                                data?.intakeQuestions?.reason ||
                                "-"}
                              </td>
                              {selectedStatus?.value !== "finished" && (
                                <td style={{ textAlign: "center" }}>
                                  {waitTime} m
                                </td>
                              )}
                              {selectedStatus?.value === "finished" && (
                                <td style={{ textAlign: "center" }}>
                                  {ViewDate(data?.createdAt)}
                                </td>
                              )}
                              <td style={{ textAlign: "center" }}>
                                {data?.notes?.length > 0 ? (
                                  <Tooltip
                                    title={
                                      <>
                                        Note:{" "}
                                        {data.notes[data.notes.length - 1]?.note}
                                        <br />
                                        <br />
                                        Added by:{" "}
                                        {capitalizeWords(
                                          data.notes[data.notes.length - 1]?.actor
                                            ?.display
                                        )}
                                        <br />
                                        Timestamp:{" "}
                                        {moment(
                                          data.notes[data.notes.length - 1]
                                            ?.updatedAt
                                        ).format("DD-MMM-YY HH:mm")}
                                      </>
                                    }
                                  >
                                    <span style={{ cursor: "pointer" }}>📄</span>
                                  </Tooltip>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <div
                                  className="action-wrap"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {data?.intakeQuestions?.preferredChannel === "phone" || data?.intakeQuestions?.preferredChannel === "video/chat" ? (
                                    data?.intakeQuestions?.preferredChannel === "phone" ? (
                                      <img src={Audio} alt="Audio Channel" />
                                    ) : (
                                      <img src={Video} alt="Video Channel" />
                                    )
                                  ) : "-"}
                                </div>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {data?.practitionerHistory &&
                                data?.practitionerHistory?.[
                                  data?.practitionerHistory?.length - 1
                                ]?.actor?.display &&
                                data?.practitionerHistory?.[
                                  data?.practitionerHistory?.length - 1
                                ]?.actor?.display !== "-"
                                  ? capitalizeWords(
                                      data?.practitionerHistory[
                                        data?.practitionerHistory.length - 1
                                      ]?.actor?.display
                                    )
                                  : "-"}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <Tooltip title="View details">
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleViewClick(data)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleViewClick(data);
                                      }
                                    }}
                                    className="ops-view-btn"
                                    style={{
                                      cursor: "pointer",
                                      color: "#005da8",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <VisibilityOutlined />
                                  </span>
                                </Tooltip>
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <TableCell colSpan={11} style={{ textAlign: "center" }}>
                          No Data Available
                        </TableCell>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {searchFilterData?.length > 0 ? (
                <CustomPagination
                  tableData={searchFilterData}
                  totalRows={searchFilterData?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  handlePage={(pageNo) => setPage(pageNo)}
                />
              ) : null}
            </div>
          </>
        )}
      </section>
      <OpsViewQueueModal
        data={selectedRecord}
        show={viewModalShow}
        onHide={() => {
          setViewModalShow(false);
          setSelectedRecord(null);
        }}
        orgName={selectedOrgName}
      />
    </div>
  );
};

export default OpsDashboardQueue;
